import React, { useEffect, useRef, useState } from 'react';
import type { ObserveStreamEvent, StreamStatus } from './useApiObserve';

type Props = {
  events: ObserveStreamEvent[];
  status: StreamStatus;
  paused: boolean;
  onTogglePause: () => void;
};

function formatTime(iso: string) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '--:--:--';
  return d.toLocaleTimeString(undefined, { hour12: false });
}

export default function LiveEventFeed({ events, status, paused, onTogglePause }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  useEffect(() => {
    if (!autoScroll || paused) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events, autoScroll, paused]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  };

  const statusLabel =
    status === 'connected' ? (
      <span className="text-green-600 text-sm font-medium">● Live</span>
    ) : status === 'reconnecting' ? (
      <span className="text-amber-600 text-sm font-medium">○ Reconnecting...</span>
    ) : (
      <span className="text-gray-500 text-sm">○ Offline</span>
    );

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
        <span className="text-sm font-medium text-gray-800">Live event stream</span>
        <div className="flex items-center gap-3">
          {statusLabel}
          <button
            type="button"
            onClick={onTogglePause}
            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-100"
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="h-64 overflow-y-auto font-mono text-xs divide-y divide-gray-100"
      >
        {events.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Waiting for events…</div>
        ) : (
          events.map((ev) => (
            <div key={`${ev.id}-${ev.time}`} className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50">
              <span className="text-gray-500 w-16 flex-shrink-0">{formatTime(ev.time)}</span>
              <span
                className="w-2 h-2 rounded-full mt-1 flex-shrink-0"
                style={{ backgroundColor: ev.success ? '#22c55e' : '#ef4444' }}
              />
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                  ev.type === 'mcp' || ev.type === 'external_api'
                    ? ev.type === 'mcp'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100'
                }`}
              >
                {ev.type === 'mcp' ? 'MCP' : 'API'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-gray-900 truncate">{ev.label}</div>
                <div className="text-gray-500 flex flex-wrap gap-2">
                  {ev.app_id && <span>{ev.app_id}</span>}
                  {ev.latency_ms != null && <span>{ev.latency_ms} ms</span>}
                  {ev.error_message && (
                    <span className="truncate max-w-md" title={ev.error_message}>
                      {ev.error_message.length > 60
                        ? `${ev.error_message.slice(0, 60)}…`
                        : ev.error_message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
